import axios from 'axios';
import qs from 'qs';
import { toast } from 'react-toastify';
import config from "../config";
import { buildDirectStripePaidUpdate } from '../utils/tipPayoutPaidStatus';

const REBELITY_ADMIN_ROLE = 'Admin';

function isRebelityPlatformAdminFromState(authState = {}) {
  if (authState.isRebelityAdmin) {
    return true;
  }

  const roles = Array.isArray(authState.identityRoles) ? authState.identityRoles : [];
  return roles.includes(REBELITY_ADMIN_ROLE);
}

export const RECEIVED_USERS = 'RECEIVED_USERS';
export const RECEIVING_USERS = 'RECEIVING_USERS';
export const RECEIVED_USER = 'RECEIVED_USER';
export const RECEIVING_USER = 'RECEIVING_USER';
export const UPDATED_USER = 'UPDATED_USER';
export const CREATED_USER = 'CREATED_USER';
export const UPDATING_USER = 'UPDATING_USER';
export const DELETED_USER = 'DELETED_USER';
export const DELETING_USER = 'DELETING_USER';
export const FAILURE_USER = 'FAILURE_USER';
export const SET_ROLE_ID = 'SET_ROLE_ID';

// Employee Sales actions
export const RECEIVING_EMPLOYEE_SALES = 'RECEIVING_EMPLOYEE_SALES';
export const RECEIVED_EMPLOYEE_SALES = 'RECEIVED_EMPLOYEE_SALES';
export const CLEAR_EMPLOYEE_SALES = 'CLEAR_EMPLOYEE_SALES';
export const SET_SELECTED_EMPLOYEE = 'SET_SELECTED_EMPLOYEE';

// Employee Payout actions
export const RECEIVING_PAYOUT_STATUS = 'RECEIVING_PAYOUT_STATUS';
export const RECEIVED_PAYOUT_STATUS = 'RECEIVED_PAYOUT_STATUS';
export const RECEIVING_PAYOUT_HISTORY = 'RECEIVING_PAYOUT_HISTORY';
export const RECEIVED_PAYOUT_HISTORY = 'RECEIVED_PAYOUT_HISTORY';
export const RETRYING_PAYOUT = 'RETRYING_PAYOUT';
export const RETRY_PAYOUT_COMPLETE = 'RETRY_PAYOUT_COMPLETE';
export const TRIGGERING_PAYOUT = 'TRIGGERING_PAYOUT';
export const TRIGGER_PAYOUT_COMPLETE = 'TRIGGER_PAYOUT_COMPLETE';
export const RETRYING_FAILED_TRANSFERS = 'RETRYING_FAILED_TRANSFERS';
export const RETRY_FAILED_TRANSFERS_COMPLETE = 'RETRY_FAILED_TRANSFERS_COMPLETE';
export const SKIPPING_TRANSFERS = 'SKIPPING_TRANSFERS';
export const SKIP_TRANSFERS_COMPLETE = 'SKIP_TRANSFERS_COMPLETE';
export const RECLAIMING_TRANSFERS = 'RECLAIMING_TRANSFERS';
export const RECLAIM_TRANSFERS_COMPLETE = 'RECLAIM_TRANSFERS_COMPLETE';
export const RECLAIMING_TO_PLATFORM = 'RECLAIMING_TO_PLATFORM';
export const RECLAIM_TO_PLATFORM_COMPLETE = 'RECLAIM_TO_PLATFORM_COMPLETE';
export const PROCESSING_HELD_REVERSALS = 'PROCESSING_HELD_REVERSALS';
export const PROCESS_HELD_REVERSALS_COMPLETE = 'PROCESS_HELD_REVERSALS_COMPLETE';
export const UPDATE_SALE_PAYOUT_STATUS = 'UPDATE_SALE_PAYOUT_STATUS';
export const RECEIVING_TIP_FUNDING_SUMMARY = 'RECEIVING_TIP_FUNDING_SUMMARY';
export const RECEIVED_TIP_FUNDING_SUMMARY = 'RECEIVED_TIP_FUNDING_SUMMARY';

const LOCAL_PAID_WORKPERIODS_KEY = 'bistro.paidWorkperiods';
const LOCAL_MANUAL_PAY_REASONS_KEY = 'bistro.manualPayReasons';
const WAITING_FOR_FUNDS_TO_CLEAR_LABEL = 'Waiting for Funds to Clear';

function readLocalPaidWorkperiods() {
    if (typeof window === 'undefined' || !window.localStorage) return {};

    try {
        const raw = window.localStorage.getItem(LOCAL_PAID_WORKPERIODS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
        return {};
    }
}

function writeLocalPaidWorkperiods(map) {
    if (typeof window === 'undefined' || !window.localStorage) return;

    try {
        window.localStorage.setItem(LOCAL_PAID_WORKPERIODS_KEY, JSON.stringify(map));
    } catch (error) {
        // Ignore storage failures. This cache is only a UI fallback.
    }
}

function getLocalPaidWorkperiodKey(userId, workPeriodId) {
    const normalizedUserId = Number(userId);
    const normalizedWorkPeriodId = Number(workPeriodId);
    if (!Number.isFinite(normalizedUserId) || !Number.isFinite(normalizedWorkPeriodId)) return null;
    return `${normalizedUserId}:${normalizedWorkPeriodId}`;
}

export function rememberWorkPeriodPaidLocally(userId, workPeriodId) {
    const key = getLocalPaidWorkperiodKey(userId, workPeriodId);
    if (!key) return;

    const existing = readLocalPaidWorkperiods();
    writeLocalPaidWorkperiods({
        ...existing,
        [key]: true,
    });
}

export function isWorkPeriodMarkedPaidLocally(userId, workPeriodId) {
    const key = getLocalPaidWorkperiodKey(userId, workPeriodId);
    if (!key) return false;

    const existing = readLocalPaidWorkperiods();
    return existing[key] === true;
}

export function clearWorkPeriodPaidLocally(userId, workPeriodId) {
    const key = getLocalPaidWorkperiodKey(userId, workPeriodId);
    if (!key) return;

    const existing = readLocalPaidWorkperiods();
    if (!existing[key]) return;

    const next = { ...existing };
    delete next[key];
    writeLocalPaidWorkperiods(next);
}

/** Drop stale local "paid" flags when the API still reports unpaid/partial. */
export function syncLocalPaidFlagsFromEmployeeSales(userId, sales) {
    if (!userId || !Array.isArray(sales)) return;

    const unpaidStatuses = new Set(['failed', 'pending', 'partial', 'not_eligible', 'reversed']);
    sales.forEach((row) => {
        if (!row) return;
        const status = String(row.TipPayoutStatus || row.tipPayoutStatus || '').toLowerCase();
        if (!unpaidStatuses.has(status)) return;

        const workPeriodId = row.WorkPeriodId ?? row.workPeriodId;
        if (workPeriodId) {
            clearWorkPeriodPaidLocally(userId, workPeriodId);
        }
    });
}

function readLocalManualPayReasons() {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    try {
        const raw = window.localStorage.getItem(LOCAL_MANUAL_PAY_REASONS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
        return {};
    }
}

function writeLocalManualPayReasons(map) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(LOCAL_MANUAL_PAY_REASONS_KEY, JSON.stringify(map));
    } catch (error) {
        // Ignore storage failures. This cache is only a UI fallback.
    }
}

export function rememberManualPayReasonLocally(userId, workPeriodId, reason) {
    const key = getLocalPaidWorkperiodKey(userId, workPeriodId);
    if (!key || !reason) return;
    const existing = readLocalManualPayReasons();
    writeLocalManualPayReasons({ ...existing, [key]: reason });
}

export function getLocalManualPayReason(userId, workPeriodId) {
    const key = getLocalPaidWorkperiodKey(userId, workPeriodId);
    if (!key) return null;
    const existing = readLocalManualPayReasons();
    return existing[key] || null;
}

function normalizeTransferCount(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function normalizePayoutMessage(message) {
    if (!message) return '';

    return String(message)
        .replace(/waiting for available platform funds/ig, WAITING_FOR_FUNDS_TO_CLEAR_LABEL)
        .replace(/waiting for platform funds/ig, WAITING_FOR_FUNDS_TO_CLEAR_LABEL)
        .replace(/platform balance is not yet available for this payout/ig, `This payout is marked "${WAITING_FOR_FUNDS_TO_CLEAR_LABEL}"`)
        .replace(/platform balance clears/ig, 'funds clear')
        .replace(/the platform balance clears/ig, 'funds clear')
        .replace(/awaiting available funds/ig, WAITING_FOR_FUNDS_TO_CLEAR_LABEL)
        .replace(/still awaiting funds/ig, `still marked "${WAITING_FOR_FUNDS_TO_CLEAR_LABEL}"`)
        .replace(/still awaiting available funds/ig, `still marked "${WAITING_FOR_FUNDS_TO_CLEAR_LABEL}"`);
}

function hasNoUnpaidTransfers(result) {
    const message = String(result?.message || '').toLowerCase();
    return message.includes('no unpaid transfers found') || message.includes('no unpaid transfer found');
}

function isPayoutApiSuccess(status, data = {}) {
    if (status === 'Ok' || status === 'Partial') {
        return true;
    }

    return hasNoUnpaidTransfers(data);
}

function normalizePayoutAttemptResult(result = {}) {
    const merchantFundedTransferIds = Array.isArray(result.merchantFundedTransferIds)
        ? result.merchantFundedTransferIds.map(id => Number(id)).filter(id => Number.isFinite(id))
        : [];
    const heldTransferIds = Array.isArray(result.heldTransferIds)
        ? result.heldTransferIds.map(id => Number(id)).filter(id => Number.isFinite(id))
        : [];
    return {
        status: result.status || null,
        message: normalizePayoutMessage(result.message || ''),
        totalAmount: Number(result.totalAmount) || 0,
        transfersCompleted: normalizeTransferCount(result.transfersCompleted),
        transfersFailed: normalizeTransferCount(result.transfersFailed),
        canRetryFailedTransfers: !!result.canRetryFailedTransfers,
        failedTransfersAvailable: normalizeTransferCount(result.failedTransfersAvailable),
        pendingTransfersAvailable: normalizeTransferCount(result.pendingTransfersAvailable),
        transfersPending: normalizeTransferCount(result.transfersPending),
        merchantFundedCount: normalizeTransferCount(result.merchantFundedCount),
        merchantFundedAmount: Number(result.merchantFundedAmount) || 0,
        merchantFundedTransferIds,
        hasMerchantFundedTransfers: !!result.hasMerchantFundedTransfers || merchantFundedTransferIds.length > 0,
        heldCount: normalizeTransferCount(result.heldCount),
        heldAmount: Number(result.heldAmount) || 0,
        heldTransferIds,
        hasHeldTransfers: !!result.hasHeldTransfers || heldTransferIds.length > 0,
    };
}

function deriveSalePayoutStatus(result) {
    if (!result) return null;

    if (hasNoUnpaidTransfers(result)) {
        return 'paid';
    }

    if (result.transfersFailed > 0 || result.failedTransfersAvailable > 0) {
        if (result.transfersCompleted > 0 || result.transfersPending > 0 || result.pendingTransfersAvailable > 0) {
            return 'partial';
        }

        return 'failed';
    }

    if (result.transfersPending > 0 || result.pendingTransfersAvailable > 0) {
        return 'pending';
    }

    if (result.heldCount > 0 || result.hasHeldTransfers) {
        if (result.transfersCompleted > 0) {
            return 'partial';
        }

        return 'pending';
    }

    if (result.transfersCompleted > 0) {
        if (result.status === 'Partial' || result.transfersFailed > 0) {
            return 'partial';
        }

        return 'paid';
    }

    if (result.status === 'Partial') {
        return 'partial';
    }

    return null;
}

function showPayoutAttemptToast(result, isRetry) {
    const actionLabel = isRetry ? 'Retry' : 'Payout';

    if (hasNoUnpaidTransfers(result)) {
        toast.info(result.message || 'No unpaid transfers found for this work period.');
        return;
    }

    if (result.status === 'Partial' && result.transfersCompleted > 0) {
        toast.warning(result.message || `${actionLabel} partially completed. Review remaining transfers before retrying.`);
        return;
    }

    if (result.failedTransfersAvailable > 0 && result.canRetryFailedTransfers) {
        const failedCount = result.transfersFailed;
        toast.warning(
            result.message || `${actionLabel} finished with ${failedCount} failed transfer${failedCount === 1 ? '' : 's'}. Retry is available.`
        );
        return;
    }

    if (result.transfersFailed > 0) {
        const failedCount = result.transfersFailed;
        toast.warning(
            result.message || `${actionLabel} finished with ${failedCount} failed transfer${failedCount === 1 ? '' : 's'}.`
        );
        return;
    }

    if (result.transfersPending > 0 || result.pendingTransfersAvailable > 0) {
        toast.info(result.message || `${actionLabel} is pending.`);
        return;
    }

    if (result.transfersCompleted > 0) {
        toast.success(result.message || `${actionLabel} of $${result.totalAmount.toFixed(2)} completed!`);
        return;
    }

    if (result.status === 'Error') {
        toast.error(result.message || `Failed to ${isRetry ? 'retry failed transfers' : 'trigger payout'}`);
        return;
    }

    toast.info(result.message || `${actionLabel} processed.`);
}

function updateSalePayoutStatusIfNeeded(dispatch, saleId, workPeriodId, result, options = {}) {
    const nextStatus = deriveSalePayoutStatus(result);
    const recipientDisplayName = options.recipientDisplayName;
    const userId = options.userId;

    if (!(saleId || workPeriodId) || !nextStatus) {
        return;
    }

    const totalAmount = Number(result?.totalAmount) || 0;
    let payload = { saleId, workPeriodId, status: nextStatus };

    if (recipientDisplayName && (nextStatus === 'paid' || nextStatus === 'partial') && totalAmount > 0) {
        const directUpdate = buildDirectStripePaidUpdate(
            userId,
            workPeriodId,
            saleId,
            recipientDisplayName,
            totalAmount,
            options.amountOwedBefore,
        );
        if (directUpdate) {
            payload = {
                saleId,
                workPeriodId,
                status: directUpdate.status,
                manualPayReason: directUpdate.manualPayReason,
                tipsPaidAmount: directUpdate.tipsPaidAmount,
                tipsOwedAmount: directUpdate.tipsOwedAmount,
            };
        }
    }

    dispatch({
        type: UPDATE_SALE_PAYOUT_STATUS,
        payload: {
            ...payload,
            userId: userId || payload.userId,
        },
    });

    if (payload.status === 'paid' && userId && workPeriodId) {
        rememberWorkPeriodPaidLocally(userId, workPeriodId);
    } else if (payload.status && payload.status !== 'paid' && userId && workPeriodId) {
        clearWorkPeriodPaidLocally(userId, workPeriodId);
    }

    if (payload.manualPayReason && userId && workPeriodId) {
        rememberManualPayReasonLocally(userId, workPeriodId, payload.manualPayReason);
    }
}

const EMPLOYEE_SALES_REFETCH_LOOKBACK_DAYS = 90;
const EMPLOYEE_SALES_SILENT_REFETCH_DELAY_MS = 250;

function buildEmployeeSalesDateRange() {
    const now = new Date();
    const endDate = now.toISOString().split('T')[0];
    const past = new Date();
    past.setDate(past.getDate() - EMPLOYEE_SALES_REFETCH_LOOKBACK_DAYS);
    return {
        startDate: past.toISOString().split('T')[0],
        endDate,
    };
}

function employeeSalesBelongToUser(sales, userId) {
    const uid = Number(userId);
    if (!Number.isFinite(uid) || !Array.isArray(sales) || sales.length === 0) {
        return false;
    }

    return sales.some((row) => {
        const rowId = row?.Id ?? row?.id;
        if (typeof rowId === 'string' && rowId.startsWith(`${uid}-`)) {
            return true;
        }

        const rowUserId = row?.UserId ?? row?.userId ?? row?.OrderUserId ?? row?.orderUserId;
        return rowUserId != null && Number(rowUserId) === uid;
    });
}

function isEmployeeSalesContextActive(state, userId) {
    return employeeSalesBelongToUser(state?.users?.employeeSales, userId);
}

function scheduleEmployeeSalesRefetch(userIds) {
    return (dispatch, getState) => {
        const normalizedUserIds = Array.from(new Set(
            (userIds || [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id)),
        ));

        if (normalizedUserIds.length === 0) {
            return;
        }

        const state = getState();
        const activeUserIds = normalizedUserIds.filter((userId) => (
            isEmployeeSalesContextActive(state, userId)
        ));

        if (activeUserIds.length === 0) {
            return;
        }

        const { startDate, endDate } = buildEmployeeSalesDateRange();
        const schedule = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
            ? window.setTimeout.bind(window)
            : (fn) => fn();

        activeUserIds.forEach((userId, index) => {
            schedule(() => {
                if (!isEmployeeSalesContextActive(getState(), userId)) {
                    return;
                }

                dispatch(getEmployeeSalesRequest(userId, startDate, endDate, { silent: true }));
            }, index * EMPLOYEE_SALES_SILENT_REFETCH_DELAY_MS);
        });
    };
}

export function applyTipObligationPaidUpdates(updates) {
    return (dispatch) => {
        if (!Array.isArray(updates) || updates.length === 0) {
            return;
        }

        updates.forEach((update) => {
            if (!update) {
                return;
            }

            const userId = update.userId;
            const workPeriodId = update.workPeriodId;
            const status = update.status || 'paid';

            dispatch({
                type: UPDATE_SALE_PAYOUT_STATUS,
                payload: {
                    userId,
                    saleId: update.saleId,
                    workPeriodId,
                    status,
                    manualPayReason: update.manualPayReason,
                    tipsPaidAmount: update.tipsPaidAmount,
                    tipsOwedAmount: update.tipsOwedAmount,
                },
            });

            if (userId && workPeriodId) {
                if (status === 'paid') {
                    rememberWorkPeriodPaidLocally(userId, workPeriodId);
                } else {
                    // Partial / failed attempts must not leave a sticky local "paid" badge.
                    clearWorkPeriodPaidLocally(userId, workPeriodId);
                }
                if (update.manualPayReason) {
                    rememberManualPayReasonLocally(userId, workPeriodId, update.manualPayReason);
                }
            }
        });

        dispatch(scheduleEmployeeSalesRefetch(updates.map((update) => update.userId)));
    };
}

export function setRoleId(id) {
    return (dispatch) => {
        dispatch({
            type: SET_ROLE_ID,
            payload: id
        });
    };
}

async function getRoles(merchantId) {
    const response = await axios.get(`/api/user/roles/${merchantId}`);
    if (response?.data?.status === 'Error') {
        throw new Error(response.data.message || 'Failed to load roles');
    }
    const roles = response?.data?.roles ?? response?.data?.Roles;
    return Array.isArray(roles) ? roles : [];
}

export function getUsersRequest(merchantId) {
    return async (dispatch, getState) => {
        dispatch(receivingUsers());

        if (!merchantId) {
            dispatch(failtureUser('Merchant information is unavailable.'));
            return;
        }

        try {
            let roles = await getRoles(merchantId);
            const res = await axios.get(`/api/useradmin/users`);
            if (res?.data?.status === 'Error') {
                dispatch(failtureUser(
                    res.data.detail
                        ? `${res.data.message || 'Failed to load users'} (${res.data.detail})`
                        : (res.data.message || 'Failed to load users')
                ));
                return;
            }
            let users = res?.data?.users ?? res?.data?.Users;
            if (!Array.isArray(users)) {
                users = [];
            }
            // Normalize Roles so role-tab filtering cannot crash or hide everyone.
            users = users.map((u) => {
                if (!u || Array.isArray(u.Roles)) return u;
                const rolesAlt = u.roles;
                return {
                    ...u,
                    Roles: Array.isArray(rolesAlt) ? rolesAlt : [],
                };
            });
            let payload = {
                roles,
                users
            };
            dispatch(receiveUsers(payload));

            // Keep the active role tab valid once roles arrive (default curRoleId is 1).
            const state = typeof getState === 'function' ? getState() : null;
            const curRoleId = state?.users?.curRoleId;
            if (roles.length > 0 && !roles.some((r) => r.Id == curRoleId || r.id == curRoleId)) {
                dispatch(setRoleId(roles[0].Id ?? roles[0].id));
            }
        } catch (error) {
            console.log(error);
            dispatch(failtureUser(error?.response?.data?.message || error.message || 'Failed to load users'));
        }
    };
}

export function loadUserRequest(id) {
    return (dispatch) => {
        dispatch(receivingUser());
        axios.get('/api/useradmin/' + id).then(res => {
            console.log("user");
            console.log(res);
            //dispatch(receiveUser(res.data));
        })
    };
}

export function updateUserRequest(user) {
    return (dispatch) => {
        dispatch(updatingUser());
        axios.put('/api/useradmin/update', user).then(res => {
            if (res.data.status == "Error") {
                toast.error(res.data.message);
                dispatch(failtureUser(res.data.message));
            } else {
                dispatch(updateUser(user));
                toast.success("User has been Updated!");
            }
        })
    };
}

export function createUserRequest(user) {
    return (dispatch) => {
        dispatch(updatingUser());
        axios.post('/api/useradmin/add', user).then(res => {
            if (res.data.status == "Error") {
                toast.error(res.data.message);
                dispatch(failtureUser(res.data.message));
            } else {
                dispatch(createUser(res.data.user));
                toast.success("User has been Created!");
            }
        })
    };
}

export function deleteUserRequest(id) {
    return (dispatch) => {
        dispatch(deletingUser(id));
        axios.delete('/api/useradmin/delete/' + id).then(res => {
            if (res.data.status == "Error") {
                toast.error(res.data.message);
                dispatch(failtureUser(res.data.message));
            } else {
                dispatch(deleteUser(id));
                toast.success("User has been Deleted!");
            }
        })
    };
}

export function receiveUsers(payload) {
    console.log(payload);
    return {
        type: RECEIVED_USERS,
        payload
    }
}

export function receivingUsers() {
    return {
        type: RECEIVING_USERS
    }
}

export function receiveUser(payload) {
    return {
        type: RECEIVED_USER,
        payload
    }
}

export function receivingUser() {
    return {
        type: RECEIVING_USER
    }
}

export function createUser(payload) {
    return {
        type: CREATED_USER,
        payload
    }
}

export function updateUser(payload) {
    return {
        type: UPDATED_USER,
        payload
    }
}

export function updatingUser() {
    return {
        type: UPDATING_USER
    }
}

export function deleteUser(payload) {
    return {
        type: DELETED_USER,
        payload
    }
}

export function deletingUser(payload) {
    return {
        type: DELETING_USER,
        payload
    }
}

export function failtureUser(payload) {
    return {
        type: FAILURE_USER,
        payload
    }
}

// Employee Sales actions
export function getEmployeeSalesRequest(userId, startDate, endDate, options = {}) {
    const silent = !!options.silent;

    return (dispatch, getState) => {
        const uid = Number(userId);
        if (!Number.isFinite(uid)) {
            return;
        }

        if (silent && !isEmployeeSalesContextActive(getState(), uid)) {
            return;
        }

        if (!silent) {
            dispatch({ type: RECEIVING_EMPLOYEE_SALES });
        }

        axios.get(`/api/useradmin/sales/${uid}`, {
            params: { startDate, endDate },
        }).then((res) => {
            if (silent && !isEmployeeSalesContextActive(getState(), uid)) {
                return;
            }

            if (res.data.status === 'Error') {
                if (!silent) {
                    toast.error(res.data.message);
                    dispatch({
                        type: RECEIVED_EMPLOYEE_SALES,
                        payload: {
                            sales: [],
                            summary: null,
                        },
                    });
                }
                return;
            }

            const sales = res.data.sales || [];
            syncLocalPaidFlagsFromEmployeeSales(uid, sales);
            dispatch({
                type: RECEIVED_EMPLOYEE_SALES,
                payload: {
                    sales,
                    summary: res.data.summary || null,
                },
            });
        }).catch((err) => {
            if (silent) {
                console.warn('Background employee sales refresh failed:', err);
                return;
            }

            console.error('Error fetching employee sales:', err);
            toast.error('Failed to load sales data');
            dispatch({
                type: RECEIVED_EMPLOYEE_SALES,
                payload: {
                    sales: [],
                    summary: null,
                },
            });
        });
    };
}

export function setSelectedEmployee(employee) {
    return {
        type: SET_SELECTED_EMPLOYEE,
        payload: employee
    };
}

export function clearEmployeeSales() {
    return {
        type: CLEAR_EMPLOYEE_SALES
    };
}

export function sendEmployeeSalesReportEmailRequest(userId, payload) {
    return () => axios.post(`/api/useradmin/sales/${userId}/send-report-email`, {
        UserId: userId,
        Email: payload.email,
        Username: payload.username,
        StoreName: payload.storeName,
        StartDate: payload.startDate,
        EndDate: payload.endDate,
        PeriodLabel: payload.periodLabel,
        FileName: payload.fileName,
        PdfBase64: payload.pdfBase64,
    }).then((res) => {
        if (res.data.status === 'Error') {
            throw new Error(res.data.message || 'Failed to send sales report email.');
        }
        return res.data;
    });
}

export function sendEmployeeSalesReportSmsRequest(userId, payload) {
    return () => axios.post(`/api/useradmin/sales/${userId}/send-report-sms`, {
        UserId: userId,
        Phone: payload.phone,
        Username: payload.username,
        StoreName: payload.storeName,
        StartDate: payload.startDate,
        EndDate: payload.endDate,
        PeriodLabel: payload.periodLabel,
        FileName: payload.fileName,
        PdfBase64: payload.pdfBase64,
    }).then((res) => {
        if (res.data.status === 'Error') {
            throw new Error(res.data.message || 'Failed to send sales report SMS.');
        }
        return res.data;
    });
}

// Get payout eligibility status for an employee.
// We tag both the loading marker and the resolved payload with the requested
// userId so consumers (e.g. WorkperiodDetails) can ignore stale responses
// from a previously-viewed user. This prevents the "Send Setup Link" panel
// from flashing on connected users while the in-flight request is resolving.
export function getPayoutStatusRequest(userId) {
    return (dispatch) => {
        dispatch({ type: RECEIVING_PAYOUT_STATUS, payload: { userId } });

        axios.get(`/api/stripe/connect/status/${userId}`).then(res => {
            if (res.data.status === "Error") {
                dispatch({
                    type: RECEIVED_PAYOUT_STATUS,
                    payload: { _userId: userId }
                });
            } else {
                const data = res.data.account || res.data;
                dispatch({
                    type: RECEIVED_PAYOUT_STATUS,
                    payload: { ...data, _userId: userId }
                });
            }
        }).catch(err => {
            console.error('Error fetching payout status:', err);
            dispatch({
                type: RECEIVED_PAYOUT_STATUS,
                payload: { _userId: userId }
            });
        });
    };
}

// Get detailed payout history for an employee
export function getPayoutHistoryRequest(userId, startDate, endDate) {
    return (dispatch) => {
        dispatch({ type: RECEIVING_PAYOUT_HISTORY });
        
        axios.get(`/api/stripe/connect/payouts/employee/${userId}`, {
            params: { startDate, endDate }
        }).then(res => {
            if (res.data.status === "Error") {
                dispatch({ 
                    type: RECEIVED_PAYOUT_HISTORY, 
                    payload: null 
                });
            } else {
                dispatch({ 
                    type: RECEIVED_PAYOUT_HISTORY, 
                    payload: res.data.history || null
                });
            }
        }).catch(err => {
            console.error('Error fetching payout history:', err);
            dispatch({ 
                type: RECEIVED_PAYOUT_HISTORY, 
                payload: null 
            });
        });
    };
}

// Retry a failed payout transfer
export function retryPayoutRequest(transferId, saleId) {
    return (dispatch) => {
        dispatch({ type: RETRYING_PAYOUT, payload: transferId });
        
        axios.post(`/api/stripe/connect/payouts/${transferId}/retry`).then(res => {
            if (res.data.status === "Error") {
                toast.error(res.data.message || 'Failed to retry payout');
                dispatch({ 
                    type: RETRY_PAYOUT_COMPLETE, 
                    payload: { transferId, success: false }
                });
            } else {
                toast.success('Payout retry successful!');
                dispatch({ 
                    type: RETRY_PAYOUT_COMPLETE, 
                    payload: { transferId, success: true, newStatus: res.data.newStatus }
                });
                // Update the sale's payout status
                if (saleId && res.data.newStatus === 'completed') {
                    dispatch({
                        type: UPDATE_SALE_PAYOUT_STATUS,
                        payload: { saleId, status: 'paid' }
                    });
                }
            }
        }).catch(err => {
            console.error('Error retrying payout:', err);
            toast.error('Failed to retry payout');
            dispatch({ 
                type: RETRY_PAYOUT_COMPLETE, 
                payload: { transferId, success: false }
            });
        });
    };
}

// Trigger payout for a specific workperiod
export function triggerPayoutRequest(userId, workPeriodId, saleId, recipientDisplayName) {
    return (dispatch) => {
        dispatch({ type: TRIGGERING_PAYOUT, payload: saleId });
        
        axios.post('/api/stripe/connect/payouts/trigger', {
            userId,
            workPeriodId
        }).then(res => {
            const payoutResult = normalizePayoutAttemptResult(res.data);

            showPayoutAttemptToast(payoutResult, false);
            updateSalePayoutStatusIfNeeded(dispatch, saleId, workPeriodId, payoutResult, {
                userId,
                recipientDisplayName,
            });
            if (deriveSalePayoutStatus(payoutResult) === 'paid') {
                rememberWorkPeriodPaidLocally(userId, workPeriodId);
            }

            dispatch({ 
                type: TRIGGER_PAYOUT_COMPLETE, 
                payload: {
                    saleId,
                    workPeriodId,
                    success: isPayoutApiSuccess(res.data.status, res.data),
                    payoutResult,
                }
            });
        }).catch(err => {
            console.error('Error triggering payout:', err);
            toast.error('Failed to trigger payout');
            dispatch({ 
                type: TRIGGER_PAYOUT_COMPLETE, 
                payload: { saleId, workPeriodId, success: false }
            });
        });
    };
}

export function retryFailedTransfersRequest(userId, workPeriodId, saleId, recipientDisplayName) {
    return (dispatch) => {
        dispatch({ type: RETRYING_FAILED_TRANSFERS, payload: workPeriodId });

        axios.post('/api/stripe/connect/payouts/retry-failed', {
            userId,
            workPeriodId
        }).then(res => {
            const payoutResult = normalizePayoutAttemptResult(res.data);

            showPayoutAttemptToast(payoutResult, true);
            updateSalePayoutStatusIfNeeded(dispatch, saleId, workPeriodId, payoutResult, {
                userId,
                recipientDisplayName,
            });
            if (deriveSalePayoutStatus(payoutResult) === 'paid') {
                rememberWorkPeriodPaidLocally(userId, workPeriodId);
            }

            dispatch({
                type: RETRY_FAILED_TRANSFERS_COMPLETE,
                payload: {
                    saleId,
                    workPeriodId,
                    success: isPayoutApiSuccess(res.data.status, res.data),
                    payoutResult,
                }
            });

            dispatch(scheduleEmployeeSalesRefetch([userId]));
        }).catch(err => {
            console.error('Error retrying failed transfers:', err);
            toast.error('Failed to retry failed transfers');
            dispatch({
                type: RETRY_FAILED_TRANSFERS_COMPLETE,
                payload: { saleId, workPeriodId, success: false }
            });
        });
    };
}

// Mark merchant-funded pending transfers as "skipped" (employee was already paid by other means).
export function skipTransfersRequest(userId, workPeriodId, transferIds, reason, saleId) {
    return (dispatch) => {
        dispatch({ type: SKIPPING_TRANSFERS, payload: workPeriodId });

        axios.post('/api/stripe/connect/payouts/skip-transfers', {
            userId,
            workPeriodId,
            transferIds,
            reason: reason || null,
        }).then(res => {
            const noUnpaidTransfers = hasNoUnpaidTransfers(res.data);
            const skipped = Number(res.data.transfersSkipped) || (Array.isArray(res.data.transferIds) ? res.data.transferIds.length : 0);
            const success = res.data.status === 'Ok';
            if (success) {
                if (noUnpaidTransfers && skipped === 0) {
                    toast.info(res.data.message || 'Tips for this work period are already marked as paid.');
                } else if (noUnpaidTransfers) {
                    toast.info(res.data.message || 'No unpaid transfers found for this work period.');
                } else {
                    toast.success(res.data.message || `Marked ${skipped} transfer${skipped === 1 ? '' : 's'} as paid.`);
                }
            } else {
                toast.error(res.data.message || 'Failed to mark transfers as paid');
            }

            // After skip, derive a per-workperiod payoutAttemptResult that clears the merchant-funded flags
            // so the UI updates immediately without waiting for a refetch.
            dispatch({
                type: SKIP_TRANSFERS_COMPLETE,
                payload: {
                    saleId,
                    workPeriodId,
                    success,
                    message: res.data.message || '',
                    noUnpaidTransfers,
                    skippedTransferIds: Array.isArray(res.data.transferIds) ? res.data.transferIds : [],
                    manualPayReason: reason?.trim() || '',
                }
            });

            if (success && (saleId || workPeriodId)) {
                dispatch({
                    type: UPDATE_SALE_PAYOUT_STATUS,
                    payload: { saleId, workPeriodId, status: 'paid', manualPayReason: reason?.trim() || '' }
                });
                rememberWorkPeriodPaidLocally(userId, workPeriodId);
                if (reason) rememberManualPayReasonLocally(userId, workPeriodId, reason);
            }

            if (success && userId) {
                dispatch(scheduleEmployeeSalesRefetch([userId]));
            }
        }).catch(err => {
            console.error('Error skipping transfers:', err);
            toast.error('Failed to mark transfers as paid');
            dispatch({
                type: SKIP_TRANSFERS_COMPLETE,
                payload: { saleId, workPeriodId, success: false }
            });
        });
    };
}

// Reclaim tips from merchant Stripe account and transfer to the employee.
export function reclaimAndTransferRequest(userId, workPeriodId, transferIds, saleId, recipientDisplayName) {
    return (dispatch) => {
        dispatch({ type: RECLAIMING_TRANSFERS, payload: workPeriodId });

        axios.post('/api/stripe/connect/payouts/reclaim-and-transfer', {
            userId,
            workPeriodId,
            transferIds,
        }).then(res => {
            const success = isPayoutApiSuccess(res.data.status, res.data);
            const completed = Number(res.data.transfersCompleted) || 0;
            const failed = Number(res.data.transfersFailed) || 0;
            const held = Number(res.data.transfersHeld) || 0;
            const totalAmount = Number(res.data.totalAmount) || 0;

            if (res.data.status === 'Partial' && completed > 0) {
                toast.warning(res.data.message || `${completed} transfer(s) paid; ${held} held, ${failed} failed. Do not retry completed rows.`);
            } else if (success && completed > 0 && held === 0 && failed === 0) {
                toast.success(res.data.message || `Reclaimed and transferred $${totalAmount.toFixed(2)} to employee.`);
            } else if (held > 0 && completed === 0 && failed === 0) {
                toast.info(res.data.message || `${held} transfer(s) held — merchant funds not yet available. Will retry automatically.`);
            } else if (held > 0) {
                toast.warning(res.data.message || `${completed} sent, ${held} held until funds clear, ${failed} failed.`);
            } else if (failed > 0 && completed > 0) {
                toast.warning(res.data.message || `${completed} succeeded, ${failed} failed.`);
            } else {
                toast.error(res.data.message || 'Reclaim & transfer failed.');
            }

            dispatch({
                type: RECLAIM_TRANSFERS_COMPLETE,
                payload: {
                    saleId,
                    workPeriodId,
                    success,
                    completed,
                    failed,
                    processedTransferIds: Array.isArray(res.data.transferIds) ? res.data.transferIds : [],
                    heldTransferIds: Array.isArray(res.data.heldTransferIds) ? res.data.heldTransferIds : [],
                    heldAmount: Number(res.data.heldAmount) || 0,
                }
            });

            if (completed > 0 && (saleId || workPeriodId)) {
                const payoutResult = {
                    status: res.data.status,
                    totalAmount,
                    transfersCompleted: completed,
                    transfersFailed: failed,
                    transfersPending: held,
                    heldCount: held,
                };
                updateSalePayoutStatusIfNeeded(dispatch, saleId, workPeriodId, payoutResult, {
                    userId,
                    recipientDisplayName,
                });
                if (deriveSalePayoutStatus(payoutResult) === 'paid') {
                    rememberWorkPeriodPaidLocally(userId, workPeriodId);
                }
            }

            dispatch(scheduleEmployeeSalesRefetch([userId]));
        }).catch(err => {
            console.error('Error reclaiming and transferring:', err);
            toast.error('Failed to reclaim and transfer');
            dispatch({
                type: RECLAIM_TRANSFERS_COMPLETE,
                payload: { saleId, workPeriodId, success: false }
            });
        });
    };
}

// Reclaim merchant-funded tips back to the platform balance WITHOUT paying out to
// the employee. After this completes, the rows are PlatformHeld and the standard
// "Pay out Tips" flow will drain them platform → employee.
export function reclaimToPlatformRequest(userId, workPeriodId, transferIds, saleId) {
    return (dispatch) => {
        dispatch({ type: RECLAIMING_TO_PLATFORM, payload: workPeriodId });

        axios.post('/api/stripe/connect/payouts/reclaim-to-platform', {
            userId,
            workPeriodId,
            transferIds: transferIds || [],
        }).then(res => {
            const status = res.data.status;
            const success = status === 'Ok';
            const reclaimed = Number(res.data.transfersReclaimed) || 0;
            const failed = Number(res.data.transfersFailed) || 0;
            const held = Number(res.data.transfersHeld) || 0;
            const totalReclaimed = Number(res.data.totalReclaimed) || 0;

            if (success && reclaimed > 0) {
                toast.success(res.data.message || `Reclaimed $${totalReclaimed.toFixed(2)} to platform. Click "Pay out Tips" to send to employee.`);
            } else if (held > 0 && reclaimed === 0 && failed === 0) {
                toast.info(res.data.message || `${held} transfer(s) held — merchant funds not yet available. Will retry automatically.`);
            } else if (held > 0 || (reclaimed > 0 && failed > 0)) {
                toast.warning(res.data.message || `${reclaimed} reclaimed, ${held} held, ${failed} failed.`);
            } else {
                toast.error(res.data.message || 'Reclaim to platform failed.');
            }

            dispatch({
                type: RECLAIM_TO_PLATFORM_COMPLETE,
                payload: {
                    saleId,
                    workPeriodId,
                    success,
                    reclaimed,
                    failed,
                    processedTransferIds: Array.isArray(res.data.transferIds) ? res.data.transferIds : [],
                    heldTransferIds: Array.isArray(res.data.heldTransferIds) ? res.data.heldTransferIds : [],
                    heldAmount: Number(res.data.heldAmount) || 0,
                    totalReclaimed,
                }
            });

            if (userId && (reclaimed > 0 || held > 0)) {
                dispatch(scheduleEmployeeSalesRefetch([userId]));
            }
        }).catch(err => {
            console.error('Error reclaiming to platform:', err);
            toast.error('Failed to reclaim to platform');
            dispatch({
                type: RECLAIM_TO_PLATFORM_COMPLETE,
                payload: { saleId, workPeriodId, success: false }
            });
        });
    };
}

// Retry tip reversals that are currently held (awaiting_funds).
// Used when the merchant's pending Stripe balance has cleared.
export function processHeldReversalsRequest(userId, workPeriodId, saleId) {
    return (dispatch) => {
        dispatch({ type: PROCESSING_HELD_REVERSALS, payload: workPeriodId });

        axios.post('/api/stripe/connect/payouts/process-held', {
            userId,
            workPeriodId,
        }).then(res => {
            const success = res.data.status !== 'Error';
            const completed = Number(res.data.transfersCompleted) || 0;
            const failed = Number(res.data.transfersFailed) || 0;
            const stillHeld = Number(res.data.transfersStillHeld) || 0;
            const totalAmount = Number(res.data.totalAmount) || 0;
            const resumed = !!res.data.merchantPayoutsResumed;

            if (completed > 0 && stillHeld === 0 && failed === 0) {
                toast.success(normalizePayoutMessage(res.data.message) || `Released $${totalAmount.toFixed(2)} of held tips to employee.`);
            } else if (completed > 0 && stillHeld > 0) {
                toast.warning(normalizePayoutMessage(res.data.message) || `${completed} released, ${stillHeld} still marked "${WAITING_FOR_FUNDS_TO_CLEAR_LABEL}".`);
            } else if (stillHeld > 0) {
                toast.info(normalizePayoutMessage(res.data.message) || `${stillHeld} transfer(s) are still marked "${WAITING_FOR_FUNDS_TO_CLEAR_LABEL}".`);
            } else if (failed > 0) {
                toast.error(normalizePayoutMessage(res.data.message) || `${failed} transfer(s) failed during retry.`);
            } else {
                toast.info(normalizePayoutMessage(res.data.message) || 'No held transfers to process.');
            }

            if (resumed) {
                toast.info('Merchant Stripe payout schedule has been restored.');
            }

            dispatch({
                type: PROCESS_HELD_REVERSALS_COMPLETE,
                payload: {
                    saleId,
                    workPeriodId,
                    success,
                    completed,
                    failed,
                    stillHeld,
                    processedTransferIds: Array.isArray(res.data.transferIds) ? res.data.transferIds : [],
                }
            });

            if (success && completed > 0 && saleId) {
                const nextStatus = stillHeld > 0 || failed > 0 ? 'partial' : 'paid';
                dispatch({
                    type: UPDATE_SALE_PAYOUT_STATUS,
                    payload: { saleId, status: nextStatus }
                });
            }

            if (userId) {
                dispatch(scheduleEmployeeSalesRefetch([userId]));
            }
        }).catch(err => {
            console.error('Error processing held reversals:', err);
            toast.error('Failed to process held transfers');
            dispatch({
                type: PROCESS_HELD_REVERSALS_COMPLETE,
                payload: { saleId, workPeriodId, success: false }
            });
        });
    };
}

export function getTipFundingSummaryRequest() {
    return (dispatch, getState) => {
        const authState = getState()?.auth || {};
        if (!isRebelityPlatformAdminFromState(authState)) {
            return Promise.resolve();
        }

        dispatch({ type: RECEIVING_TIP_FUNDING_SUMMARY });

        return axios.get('/api/stripe/connect/payouts/funding-summary').then(res => {
            if (res.data?.status === 'Ok' && res.data.summary) {
                dispatch({
                    type: RECEIVED_TIP_FUNDING_SUMMARY,
                    payload: { summary: res.data.summary, error: '' }
                });
                return;
            }

            const message = res.data?.message || '';
            const accessDenied = typeof message === 'string' && message.toLowerCase().includes('access denied');

            dispatch({
                type: RECEIVED_TIP_FUNDING_SUMMARY,
                payload: {
                    summary: null,
                    error: accessDenied ? '' : (message || 'Failed to load platform balance summary.')
                }
            });
        }).catch(err => {
            const status = err?.response?.status;
            const message = err?.response?.data?.message || '';
            const accessDenied = status === 403
                || (typeof message === 'string' && message.toLowerCase().includes('access denied'));

            if (!accessDenied) {
                console.error('Error loading tip funding summary:', err);
            }

            dispatch({
                type: RECEIVED_TIP_FUNDING_SUMMARY,
                payload: {
                    summary: null,
                    error: accessDenied ? '' : 'Failed to load platform balance summary.'
                }
            });
        });
    };
}
